"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.currencyUtilities = exports.setCommonOptions = void 0;
var currencies_1 = __importDefault(require("@incentivegames/ig-types/lib/constants/currencies"));
var currencyDisplayType_1 = require("@incentivegames/ig-types/lib/enums/currency/currencyDisplayType");
var currencies = {
    KES: 'KSh'
};
var format = function (value, locale, options) {
    return Intl.NumberFormat(locale, options).format(value);
};
exports.setCommonOptions = function (options, noDecimals) {
    options.useGrouping = true;
    if (noDecimals) {
        options.maximumFractionDigits = 0;
        options.minimumFractionDigits = 0;
    }
    else {
        options.maximumFractionDigits = 2;
        options.minimumFractionDigits = 2;
    }
    return options;
};
exports.currencyUtilities = {
    /**
     * Given a currency code returns the currency object related to that code.
     * If the `override` parameter is specified, the `code` will be checked against it first.
     * If no match is found in the overrides, or no override is specified,
     * the fallback is to search the default currency configurations.
     *
     * Note: if no matching currency is found, `null` is returned.
     *
     * @param code string. The ISO standard currency code, e.g. GBP.
     * @param overrides Optional array that can be used to override the default currency configurations.
     * @returns ICurrency. A currency object related to the currency code.
     */
    getCurrencyOrNullByCode: function (code, overrides) {
        var _a, _b;
        if (overrides === void 0) { overrides = []; }
        var findCurrency = function (currency) { return currency.code == code; };
        return (_b = (_a = overrides === null || overrides === void 0 ? void 0 : overrides.find(findCurrency)) !== null && _a !== void 0 ? _a : currencies_1.default.find(findCurrency)) !== null && _b !== void 0 ? _b : null;
    },
    /**
     * Given a currency code returns the currency object related to that code.
     * If the `override` parameter is specified, the `code` will be checked against it first.
     * If no match is found in the overrides, or no override is specified,
     * the fallback is to search the default currency configurations.
     *
     * Note: if no matching currency is found, a default value is returned:
     * ```
     * { code: '???', name: 'Unknown Currency', symbol: '???' }
     * ```
     *
     * @param code string. The ISO standard currency code, e.g. GBP.
     * @param overrides Optional array that can be used to override the default currency configurations.
     * @returns ICurrency. A currency object related to the currency code.
     */
    getCurrencyByCode: function (code, overrides) {
        if (overrides === void 0) { overrides = []; }
        var currency = this.getCurrencyOrNullByCode(code, overrides);
        return currency !== null && currency !== void 0 ? currency : { code: '???', name: 'Unknown Currency', symbol: '???' };
    },
    /**
     * Formats a numerical value as currency and returns a string, using the specified locale and options.
     *
     * @param value The number to format as string.
     * @param currency The currency to use. `PTS` is a special value we use for points that has no currency symbol.
     * @param locale The locale to use. In most cases, we can pass `config.settings.defaultLanguage`.
     * @param noDecimals This will round the number to the nearest integer and display no digits after the decimal point.
     *
     * @returns A string that is the given number formatted as currency.
     */
    formatCurrency: function (value, currency, locale, noDecimals) {
        if (noDecimals === void 0) { noDecimals = false; }
        if (currency === 'PTS') {
            return format(value, locale, exports.setCommonOptions({ style: 'decimal' }, noDecimals));
        }
        else {
            var currencySymbol = currencies[currency];
            // Some currency symbols (e.g. KES) are currently not supported correctly, so we override them.
            if (currencySymbol) {
                return currencySymbol + " " + format(value, locale, exports.setCommonOptions({ style: 'decimal' }, noDecimals));
            }
            else {
                return format(value, locale, exports.setCommonOptions({ style: 'currency', currency: currency }, noDecimals));
            }
        }
    },
    formatCurrencyWithOrientation: function (value, currency, locale, noDecimals, useSymbol, addSpacing, overrides) {
        if (noDecimals === void 0) { noDecimals = false; }
        if (useSymbol === void 0) { useSymbol = true; }
        if (addSpacing === void 0) { addSpacing = true; }
        if (overrides === void 0) { overrides = []; }
        var currentCurrency = this.getCurrencyOrNullByCode(currency, overrides);
        var spacing = addSpacing ? ' ' : '';
        return currentCurrency
            ? "" + ((currentCurrency === null || currentCurrency === void 0 ? void 0 : currentCurrency.orientation) === currencyDisplayType_1.CurrencyOrientation.Left ? (useSymbol ? currentCurrency.symbol + spacing : currentCurrency.code + spacing) : '') + format(value, locale, exports.setCommonOptions({ style: 'decimal' }, noDecimals)) + ((currentCurrency === null || currentCurrency === void 0 ? void 0 : currentCurrency.orientation) === currencyDisplayType_1.CurrencyOrientation.Right ? (useSymbol ? spacing + currentCurrency.symbol : spacing + currentCurrency.code) : '')
            : format(value, locale, exports.setCommonOptions({ style: 'decimal' }, noDecimals)) + " " + currency;
    }
};
